import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import logo from '../images/stacktalent-708X214.jpg';

const ResetPassword = () => {
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        // Supabase SDK parses the recovery hash and fires PASSWORD_RECOVERY event
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
            if (event === 'PASSWORD_RECOVERY') {
                setReady(true);
            }
        });

        // Also check if already in a recovery session
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session) setReady(true);
        });

        return () => subscription.unsubscribe();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setLoading(true);
        setError(null);

        const { error: updateError } = await supabase.auth.updateUser({ password });

        if (updateError) {
            setError(updateError.message);
            setLoading(false);
            return;
        }

        setMessage('Password updated successfully. Redirecting to sign in...');
        setTimeout(() => {
            supabase.auth.signOut().then(() => navigate('/login'));
        }, 2000);

        setLoading(false);
    };

    if (!ready) {
        return (
            <div
                className="min-vh-100 d-flex align-items-center justify-content-center"
                style={{ background: 'var(--tu-gradient-nav)' }}
            >
                <div className="text-white text-center">
                    <div className="spinner-border mb-3" />
                    <p>Validating reset link...</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-vh-100 d-flex align-items-center justify-content-center"
            style={{ background: 'var(--tu-gradient-nav)' }}
        >
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-12 col-sm-10 col-md-7 col-lg-5 col-xl-4">
                        <div className="card border-0 p-4" style={{ borderRadius: '16px', boxShadow: 'var(--tu-shadow-hover)' }}>
                            <div className="text-center mb-4">
                                <img src={logo} alt="Stacktalent" style={{ height: '36px', objectFit: 'contain' }} />
                            </div>

                            <h5 className="fw-bold mb-1" style={{ color: 'var(--tu-navy-900)' }}>Set a new password</h5>
                            <p className="small mb-4" style={{ color: 'var(--tu-text-muted)' }}>
                                Choose a strong password of at least 8 characters.
                            </p>

                            {error && <div className="alert alert-danger py-2 small mb-3">{error}</div>}
                            {message && <div className="alert alert-success py-2 small mb-3">{message}</div>}

                            {!message && (
                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold small" style={{ color: 'var(--tu-text-primary)' }}>
                                            New password
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            minLength={8}
                                            autoComplete="new-password"
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label fw-semibold small" style={{ color: 'var(--tu-text-primary)' }}>
                                            Confirm new password
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            autoComplete="new-password"
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        className="btn w-100 fw-semibold py-2"
                                        style={{
                                            background: 'var(--tu-navy-900)',
                                            color: 'var(--tu-text-on-dark)',
                                            borderRadius: '8px',
                                        }}
                                        disabled={loading}
                                    >
                                        {loading && <span className="spinner-border spinner-border-sm me-2" />}
                                        Update Password
                                    </button>
                                </form>
                            )}

                            {message && (
                                <div className="text-center mt-3">
                                    <Link to="/login" className="small text-decoration-none" style={{ color: 'var(--tu-cyan-600)' }}>
                                        Go to Sign In
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
